import { useAppDispatch } from "@/app/app.store";
import {
  useGetMyOrganizationsQuery,
  useLazyGetSessionProfileQuery,
  useSelectOrganizationMutation,
  useSwitchOrganizationMutation,
} from "@/app/features/auth/auth.api";
import {
  setOrganizationSession,
  userLoggedOut,
  writeStoredOrgSession,
} from "@/app/features/auth/auth.slice";
import type { TOrganization } from "@/app/features/auth/auth.types";
import IMAGES from "@/assets/images";
import CustomImage from "@/components/CustomImage";
import useAuth from "@/hooks/useAuth";
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  LoadingIcon,
  LogOutIcon,
} from "@/icons";
import AuthLayout from "@/layouts/AuthLayout";
import { cn } from "@/lib/cn";
import Loader from "@/pages/Loader";
import { errorToast } from "@/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router";

const isAbsoluteUrl = (url?: string | null) =>
  !!url && /^https?:\/\//.test(url);

function OrganizationLogo({ organization }: { organization: TOrganization }) {
  if (isAbsoluteUrl(organization.logo_url)) {
    return (
      <CustomImage
        size={48}
        src={organization.logo_url as string}
        className="rounded-lg object-cover bg-white"
      />
    );
  }

  return (
    <div className="size-12 shrink-0 rounded-lg bg-theme-gray-700 flex items-center justify-center text-xl font-semibold text-white">
      {organization.name?.charAt(0)?.toUpperCase() || "?"}
    </div>
  );
}

export default function SelectOrganization() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { access_token, sessionId, selectedOrganization } = useAuth();

  // A session already exists → this is a switch, not the first pick.
  const isSwitch = !!sessionId;

  const { data, isLoading } = useGetMyOrganizationsQuery(undefined, {
    skip: !access_token,
  });
  const [selectOrganization] = useSelectOrganizationMutation();
  const [switchOrganization] = useSwitchOrganizationMutation();
  const [getSessionProfile] = useLazyGetSessionProfileQuery();

  const [pendingId, setPendingId] = useState<string>();
  const autoSelected = useRef(false);

  const branch = data?.data?.branch;
  const organizations = useMemo(
    () => data?.data?.organizations ?? [],
    [data?.data?.organizations],
  );

  const handleSelect = useCallback(
    async (organization: TOrganization) => {
      if (pendingId) return;
      if (organization.id === selectedOrganization?.id) {
        navigate("/", { replace: true });
        return;
      }

      setPendingId(organization.id);
      try {
        const openSession = isSwitch ? switchOrganization : selectOrganization;
        const { data: session } = await openSession({
          organization_id: organization.id,
        }).unwrap();

        // The boot calls need this organization's employee user_id, which only
        // get-me returns.
        const profile = await getSessionProfile({
          id: session.tenant_product_user_id,
          sessionId: session.session_id,
        }).unwrap();
        const userId = profile?.data?.data?.user_id;
        if (!userId) throw new Error("Employee profile not found");

        const nextSession = {
          sessionId: session.session_id,
          userId,
          organization: {
            ...organization,
            name: session.organization_name || organization.name,
          },
        };

        if (isSwitch) {
          // Reload so no cached data from the previous organization survives.
          writeStoredOrgSession(nextSession);
          localStorage.removeItem("segments");
          window.location.replace("/");
          return;
        }

        dispatch(setOrganizationSession(nextSession));
        navigate("/", { replace: true });
      } catch (err) {
        errorToast(err, "Could not open this organization, please try again");
        setPendingId(undefined);
      }
    },
    [
      pendingId,
      selectedOrganization?.id,
      isSwitch,
      switchOrganization,
      selectOrganization,
      getSessionProfile,
      dispatch,
      navigate,
    ],
  );

  // Nothing to choose with a single organization: open it straight away.
  useEffect(() => {
    if (isSwitch || autoSelected.current || organizations.length !== 1) return;
    autoSelected.current = true;
    handleSelect(organizations[0]);
  }, [isSwitch, organizations, handleSelect]);

  const handleLogout = () => {
    dispatch(userLoggedOut());
    navigate("/login", { replace: true });
  };

  if (!access_token) return <Navigate to="/login" replace />;
  if (!isLoading && branch && branch !== "MULTI_ORG") {
    return <Navigate to="/" replace />;
  }

  // Same loader as boot while the list loads or a single company is being
  // opened; a failed auto-select falls through to the list so it can be retried.
  const isAutoSelecting =
    !isSwitch &&
    organizations.length === 1 &&
    (!autoSelected.current || !!pendingId);
  if (isLoading || isAutoSelecting) return <Loader />;

  return (
    <AuthLayout
      leftContent={
        <div className="relative z-10 flex flex-col justify-center items-center mt-32">
          <div className="text-white text-2xl font-normal">Welcome to</div>
          <div className="flex items-center justify-center mt-4 w-56 h-32">
            <img
              src={IMAGES.mainLogo}
              alt="main_logo"
              className="w-56 h-32 object-contain"
            />
          </div>
        </div>
      }
    >
      <div className="w-full min-h-full flex flex-col justify-center items-center">
        <div className="w-full max-w-[480px] flex flex-col gap-6">
          <div>
            <h1 className="text-white text-[30px] font-semibold">
              {isSwitch ? "Switch company" : "Choose a company"}
            </h1>
            <p className="text-white/50 text-base mt-1">
              Your account has access to more than one company. Pick the one you
              want to work in.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {organizations.map((organization) => {
              const isCurrent = organization.id === selectedOrganization?.id;
              const isPending = organization.id === pendingId;

              return (
                <button
                  key={organization.id}
                  type="button"
                  disabled={!!pendingId}
                  onClick={() => handleSelect(organization)}
                  className={cn(
                    "w-full flex items-center gap-4 p-4 text-left rounded-xl border transition-colors",
                    "dark:bg-theme-slate-800 dark:border-theme-gray-700 hover:border-theme-primary",
                    "disabled:cursor-not-allowed disabled:hover:border-theme-gray-700",
                    isCurrent &&
                      "border-theme-primary dark:border-theme-primary",
                  )}
                >
                  <OrganizationLogo organization={organization} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-lg font-medium truncate">
                      {organization.name}
                    </div>
                    {isCurrent && (
                      <div className="text-theme-primary text-sm">
                        Current company
                      </div>
                    )}
                  </div>
                  {isPending ? (
                    <LoadingIcon className="animate-spin text-white" />
                  ) : (
                    <ChevronRightIcon className="text-white/50" />
                  )}
                </button>
              );
            })}

            {!organizations.length && (
              <p className="text-white/50 text-center py-6">
                No company is available for this account.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between text-sm">
            {isSwitch ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-white/70 hover:text-white"
              >
                <ArrowLeftIcon size={16} />
                Back
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/70 hover:text-white"
            >
              <LogOutIcon size={16} />
              Log out
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
